import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import GoogleIcon from '@mui/icons-material/Google';
import FacebookIcon from '@mui/icons-material/Facebook';
import { login, saveSession } from '../api';
import { PROPOSE_UI_COLORS } from '../theme';
import Logo from './Logo';
import CustomerLoginForm from './CustomerLoginForm';
import { clearReturnUrl, safeReturnUrl } from '../features/hangouts/utils';

// Two sign-ins: guests (customers) with mobile + SMS code, and venue partners/admin with email +
// password (POST /api/auth/login, ported from c2n-php footer.php's login modal). ?returnUrl= (or the
// one remembered before redirecting here, e.g. from a shared invite) is where people go afterwards.
const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = safeReturnUrl(searchParams.get('returnUrl'));
  const forPartner = searchParams.get('role') === 'partner' || !!returnUrl?.match(/^\/(partner-account|admin|pos)(\/|$|\?)/);
  const [tab, setTab] = useState(forPartner ? 1 : 0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await login(email, password);
      saveSession(result.accessToken, result.email, result.isAdmin);
      clearReturnUrl();
      navigate(result.isAdmin ? '/admin' : returnUrl?.match(/^\/(partner-account|pos)(\/|$|\?)/) ? returnUrl : '/partner-account');
    } catch {
      setError('Invalid email or password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 420, mx: 'auto', mt: 16, px: 3, position: 'relative', zIndex: 2 }}>
      <Box textAlign="center" mb={4}>
        <Logo size="large" />
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth" TabIndicatorProps={{ style: { background: PROPOSE_UI_COLORS.primary } }} sx={{ mb: 3 }}>
        <Tab label="Guest" />
        <Tab label="Venue Partner" />
      </Tabs>

      {tab === 0 && (
        <CustomerLoginForm onSignedIn={() => { clearReturnUrl(); navigate(returnUrl && !returnUrl.match(/^\/(partner-account|admin|pos)(\/|$|\?)/) ? returnUrl : '/profile/invites', { replace: true }); }} />
      )}

      {tab === 1 && (
        <Box component="form" onSubmit={handleSubmit} display="flex" flexDirection="column" gap={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Email or Mobile" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <TextField label="Password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <FormControlLabel
              control={<Checkbox size="small" checked={remember} onChange={(e) => setRemember(e.target.checked)} />}
              label={<Typography fontSize={13} color="text.secondary">Remember Me</Typography>}
            />
            <Typography component={Link} to="#" fontSize={13} sx={{ color: PROPOSE_UI_COLORS.primary, textDecoration: 'none' }}>
              Forgot Password?
            </Typography>
          </Box>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Login'}
          </Button>

          <Divider sx={{ my: 1, color: 'text.secondary', fontSize: 12 }}>or continue with</Divider>
          <Box display="flex" gap={1.5} justifyContent="center">
            <IconButton sx={{ border: '1px solid rgba(245,246,248,0.15)' }}><GoogleIcon /></IconButton>
            <IconButton sx={{ border: '1px solid rgba(245,246,248,0.15)' }}><FacebookIcon sx={{ color: '#1877F2' }} /></IconButton>
          </Box>

          <Typography textAlign="center" fontSize={13} color="text.secondary">
            Own a venue and not listed yet?{' '}
            <Box component={Link} to="/partner" sx={{ color: PROPOSE_UI_COLORS.primary, fontWeight: 700, textDecoration: 'none' }}>
              Partner With Us
            </Box>
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default LoginPage;
